/**
 * Thread utility functions
 * Provides formatting and helper functions for thread data
 */

import { AiThreadWithStats, Message } from '../store/observerTypes';
import { formatTimestamp as formatChatBubbleTimestamp } from '../../utils/chatUtils';
import { format, isToday, isYesterday } from 'date-fns';

/**
 * Parse observer timestamps robustly.
 * If backend returns a timezone-less datetime string, treat it as UTC so
 * rendering via toLocaleString() shows correct user-local time.
 */
const parseObserverDate = (value: string): Date | null => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw);
  const normalized = hasTimezone ? raw : `${raw.replace(' ', 'T')}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Consolidated observer timestamp formatter (user-local display).
 */
export const formatObserverTimestamp = (dateString: string): string => {
  const date = parseObserverDate(dateString);
  if (!date) return 'N/A';

  if (isToday(date) || isYesterday(date)) {
    return formatChatBubbleTimestamp(String(Math.floor(date.getTime() / 1000)));
  }

  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return format(date, 'MMM d, p');
  }

  return format(date, 'MMM d, yyyy p');
};

/**
 * Format relative time (e.g., "2h ago", "1d ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = parseObserverDate(dateString);
  if (!date) return 'N/A';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Format absolute time with date and time
 */
export const formatAbsoluteTime = (dateString: string): string => {
  const date = parseObserverDate(dateString);
  if (!date) return 'N/A';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format date only (without time)
 */
export const formatDate = (dateString: string): string => {
  const date = parseObserverDate(dateString);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get thread status badge props
 */
export const getThreadStatusProps = (thread: AiThreadWithStats) => {
  if (thread.is_deleted) {
    return {
      label: 'Deleted',
      color: 'error' as const,
      variant: 'outlined' as const,
    };
  }
  
  if (thread.message_count === 0) {
    return {
      label: 'Empty',
      color: 'warning' as const,
      variant: 'outlined' as const,
    };
  }
  
  if (thread.pinned) {
    return {
      label: 'Pinned',
      color: 'primary' as const,
      variant: 'filled' as const,
    };
  }
  
  return {
    label: 'Active',
    color: 'success' as const,
    variant: 'outlined' as const,
  };
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

/**
 * Format message content for display
 */
export const formatMessageContent = (message: Message): string => {
  const content = message.content || '';
  
  // Remove excessive whitespace
  const cleaned = content.replace(/\s+/g, ' ').trim();
  
  // Truncate if too long
  return truncateText(cleaned, 200);
};

/**
 * Get message sender display name
 */
export const getMessageSenderName = (message: Message): string => {
  if (message.content_type === 'HUMAN_REVIEW') {
    return 'Human Review';
  }
  
  if (message.user_id) {
    return message.user_id;
  }
  
  return 'AI Assistant';
};

/**
 * Get message sender icon
 */
export const getMessageSenderIcon = (message: Message): string => {
  if (message.content_type === 'HUMAN_REVIEW') {
    return '👥';
  }
  
  if (message.user_id) {
    return '👤';
  }
  
  return '🤖';
};

/**
 * Sort threads by specified field
 */
export const sortThreads = (
  threads: AiThreadWithStats[], 
  sortBy: 'created' | 'updated' | 'messageCount', 
  order: 'ASC' | 'DESC'
): AiThreadWithStats[] => {
  return [...threads].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;
    
    switch (sortBy) {
      case 'created':
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      case 'updated':
        aValue = new Date(a.updated_at || a.created_at).getTime();
        bValue = new Date(b.updated_at || b.created_at).getTime();
        break;
      case 'messageCount':
        aValue = a.message_count;
        bValue = b.message_count;
        break;
      default:
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
    }
    
    if (order === 'ASC') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });
};

/**
 * Filter threads based on search criteria
 */
export const filterThreads = (
  threads: AiThreadWithStats[],
  searchTerm: string
): AiThreadWithStats[] => {
  if (!searchTerm.trim()) {
    return threads;
  }
  
  const term = searchTerm.toLowerCase();
  
  return threads.filter(thread => {
    return (
      (thread.user_id && thread.user_id.toLowerCase().includes(term)) ||
      (thread.ticket_id && thread.ticket_id.toLowerCase().includes(term)) ||
      (thread.thread_name && thread.thread_name.toLowerCase().includes(term)) ||
      thread.ai_thread_id.toString().includes(term)
    );
  });
};

/**
 * Get thread display name
 */
export const getThreadDisplayName = (thread: AiThreadWithStats): string => {
  if (thread.thread_name) {
    return thread.thread_name;
  }
  
  if (thread.ticket_id) {
    return `Ticket: ${thread.ticket_id}`;
  }
  
  if (thread.user_id) {
    return `User: ${thread.user_id}`;
  }
  
  return `Thread #${thread.ai_thread_id}`;
};

/**
 * Check if thread is recent (within last 24 hours)
 */
export const isRecentThread = (thread: AiThreadWithStats): boolean => {
  const threadTime = new Date(thread.updated_at || thread.created_at);
  const now = new Date();
  const diffHours = (now.getTime() - threadTime.getTime()) / (1000 * 60 * 60);
  
  return diffHours < 24;
};

/**
 * Get thread priority based on activity
 */
export const getThreadPriority = (thread: AiThreadWithStats): 'high' | 'medium' | 'low' => {
  if (thread.pinned) {
    return 'high';
  }
  
  if (isRecentThread(thread) && thread.message_count > 5) {
    return 'high';
  }
  
  if (thread.message_count > 10) {
    return 'medium';
  }
  
  return 'low';
};
